import { Subject, tap, forkJoin } from 'rxjs';
import { UsersService } from '../../../Core/Users/services/users.service';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { AuditableModel } from '../../Misc/models/auditable.model';
import { FilterTypeCheckboxModel } from '../models/filter-type-checkbox.model';
import { FilterTypeDateQueryModel } from '../models/filter-type-date-query.model';
import { FilterTypeTextModel } from '../models/filter-type-text.model';
import { FilterService } from './abstract-filter.service';
import { FilterTypeRadioModel } from '../../../Common/Filter/models/filter-type-radio.model';
import { ListEntitiesVisibilityEnum } from '../../../Common/MonitorShared/enum/list-entities-visibility.enum';
import { getDefaultDateInputOptionValuesModel } from '../../Forms/models/date-query-input-options.model';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';

export abstract class AuditableFilterService extends FilterService {
    protected datefilterBufferMinutes: number = 30;

    protected readonly nameFilter = new FilterTypeTextModel(
        'name',
        'Name',
        [],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        true
    );
    protected readonly createdByFilter = new FilterTypeCheckboxModel<number>(
        'createdBy',
        'createdBy',
        [],
        true
    );
    protected readonly modifiedByFilter = new FilterTypeCheckboxModel<number>(
        'modifiedBy',
        'modifiedBy',
        [],
        true
    );
    protected readonly createdDateFilter = new FilterTypeDateQueryModel(
        'createdDate',
        'createdDate',
        getDefaultDateInputOptionValuesModel()
    );
    protected readonly modifiedDateFilter = new FilterTypeDateQueryModel(
        'modifiedDate',
        'modifiedDate',
        getDefaultDateInputOptionValuesModel()
    );

    protected readonly visibilityFilter = new FilterTypeRadioModel<ListEntitiesVisibilityEnum>(
        'visibility',
        'Monitoring.filter.visibility',
        []
    );

    protected readonly displayGroupFilter = new FilterTypeCheckboxModel<number | undefined | null>(
        'group',
        'Monitoring.filter.groupName',
        [],
        true
    );

    private auditableFilterOptionsComplete = new Subject<void>();
    protected auditableFilterOptionsComplete$ = this.auditableFilterOptionsComplete.asObservable();

    constructor(protected usersService: UsersService, protected displayGroupsService?: DisplayGroupsService) {
        super();
        this.filters.push(
            this.createdByFilter,
            this.modifiedByFilter,
            this.createdDateFilter,
            this.modifiedDateFilter,
            this.nameFilter
        );
        this.filters$.next(this.filters);
    }

    protected applyNameFilters<T extends {name?: string}>(items: T[]): T[] {
        const nameFilters = this.nameFilter.getValue();

        if (!nameFilters || nameFilters.length < 1) {
            return items;
        }

        return items.filter(item => {
            if (!nameFilters.find(name => item.name.toLocaleLowerCase().includes(name.toLocaleLowerCase()))) {
                return false;
            }
            return true;
        });
    }

    protected getAuditableFilterOptions(items: AuditableModel[], addZeroCount: boolean = false): void {
        if (!items) {
            return;
        }
        const createdByUsersCounts: {[key: number]: number} = {};
        const modifiedByUsersCounts: {[key: number]: number} = {};
        const userIds = new Set<number>();
        const sortByCount = (a, b) => b.count - a.count;
        const hasGroupFilter = this.displayGroupsService && this.filters.find(f => f.id === this.displayGroupFilter.id);
        const byGroupFilterComplete$ = new Subject<boolean>();
        const byUseFilterComplete$ = new Subject<boolean>();

        forkJoin([byUseFilterComplete$, byGroupFilterComplete$]).subscribe(() => {
            this.auditableFilterOptionsComplete.next();
        });

        items.forEach(item => {
            userIds.add(item.createdByUserId);
            if (createdByUsersCounts[item.createdByUserId]) {
                createdByUsersCounts[item.createdByUserId]++;
            } else {
                createdByUsersCounts[item.createdByUserId] = 1;
            }
            if (item.modifiedByUserId) {
                userIds.add(item.modifiedByUserId);
                if (modifiedByUsersCounts[item.modifiedByUserId]) {
                    modifiedByUsersCounts[item.modifiedByUserId]++;
                } else {
                    modifiedByUsersCounts[item.modifiedByUserId] = 1;
                }
            }
        });

        // when the user filter is set, but the user did not create or modify any data
        // we want to show the filter with value 0
        if (addZeroCount) {
            const createdByFilters = this.createdByFilter.getValue();
            createdByFilters.forEach(createdById => {
                userIds.add(createdById);
                createdByUsersCounts[createdById] = 0;
            });

            const modifiedByFilters = this.modifiedByFilter.getValue();
            modifiedByFilters.forEach(modifiedById => {
                userIds.add(modifiedById);
                modifiedByUsersCounts[modifiedById] = 0;
            });
        }

        if (hasGroupFilter) { // group filtering
            this.displayGroupsService.getGroups().pipe(
                tap(() => {
                    this.displayGroupFilter.clear();
                    this.displayGroupFilter.loading = true;
                }),
            ).subscribe((groups) => {
                this.displayGroupFilter.options = this.displayGroupOptions(groups, items);
                this.displayGroupFilter.options.sort(sortByCount);
                this.displayGroupFilter.loading = false;

                byGroupFilterComplete$.next(true);
                byGroupFilterComplete$.complete();
            });
        } else {
            byGroupFilterComplete$.next(true);
            byGroupFilterComplete$.complete();
        }

        this.createdByFilter.loading = true;
        this.modifiedByFilter.loading = true;
        this.usersService.getUsers(Array.from(userIds)).subscribe(
            users => {
                let filtersChanged = false;
                const createdByUserIds = Object.keys(createdByUsersCounts);
                const modifiedByUserIds = Object.keys(modifiedByUsersCounts);

                this.createdByFilter.options = createdByUserIds.map((id) => {
                    const userId = +id;
                    const foundUser = users.find(user => user.id === userId);
                    const count = createdByUsersCounts[userId];

                    if (!foundUser) {
                        return null;
                    }
                    return {
                        label: foundUser.getDisplayName(),
                        value: userId,
                        count: count
                    };
                }).filter(opt => !!opt).sort(sortByCount);
                filtersChanged = filtersChanged || this.removeUnavailableOptions(this.createdByFilter);
                this.createdByFilter.loading = false;

                this.modifiedByFilter.options = modifiedByUserIds.map((id) => {
                    const userId = +id;
                    const foundUser = users.find(user => user.id === userId);
                    const count = modifiedByUsersCounts[userId];

                    if (!foundUser) {
                        return null;
                    }
                    return {
                        label: foundUser.getDisplayName(),
                        value: userId,
                        count: count
                    };
                }).filter(opt => !!opt).sort(sortByCount);

                filtersChanged = filtersChanged || this.removeUnavailableOptions(this.modifiedByFilter);
                this.modifiedByFilter.loading = false;
                if (filtersChanged || addZeroCount) {
                    this.applyFilters();
                }
                byUseFilterComplete$.next(true);
                byUseFilterComplete$.complete();
            }
        );
    }


    private displayGroupOptions(groups: DisplayGroupModel[], items: AuditableModel[]) {
        let displayGroupOptions = [];

        const groupNameMap = groups
            .filter(group => group.name)
            .reduce<Record<string, string>>((acc, group) => {
            acc[group.id] = group.name;
            return acc;
        }, {});

        // Handle different empty values (null or undefined) for groupId
        const nullGroups = [];
        const undefinedGroups = [];
        const ordinaryGroups = [];

        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.groupId === null) {
                nullGroups.push(item);
            } else if (item.groupId === undefined) {
                undefinedGroups.push(item);
            } else {
                ordinaryGroups.push(item);
            }
        }

        const countByGroup = ordinaryGroups
            .reduce<Record<number, number>>((acc, item) => {
            const groupId: number = item.groupId;
            acc[groupId] = (acc[groupId] || 0) + 1;
            return acc;
        }, {});


        displayGroupOptions = Object.entries(groupNameMap).map(([key, value]) => {
            const groupKey = Number(key);
            return {
                label: value,
                value: groupKey,
                count: countByGroup[groupKey] || 0
            };
        });

        if (undefinedGroups.length > 0) {
            displayGroupOptions.push({
                label: 'Common.lhp.groups.uncategorized',
                value: undefined,
                count: undefinedGroups.length
            });
        }

        if (nullGroups.length > 0) {
            displayGroupOptions.push({
                label: 'Common.lhp.groups.uncategorized',
                value: null,
                count: nullGroups.length
            });
        }

        return displayGroupOptions;
    }

    protected removeUnavailableOptions(filter: FilterTypeCheckboxModel): boolean {
        const items = filter.getValue();
        const val = items.filter((value) => {
            return filter.options.find(opt => opt.value === value);
        });

        if (items.length !== val.length) {
            filter.setValue(val);
            return true;
        }
        return false;
    }

    protected applyAuditableFilters<T extends AuditableModel>(items: T[]): T[] {
        items = this.applyNameFilters(items);

        const now = new Date();

        return items.filter(auditable => {

            const createdByFilters = this.createdByFilter.getValue();
            const modifiedByFilters = this.modifiedByFilter.getValue();
            const createdDateFilter = this.createdDateFilter.getValue();
            const modifiedDateFilter = this.modifiedDateFilter.getValue();
            const displayGroupNameFilters = this.displayGroupFilter.getValue();

            // The group filter has priority
            if (displayGroupNameFilters && displayGroupNameFilters.length) {
                if (!displayGroupNameFilters.includes(auditable.groupId)) {
                    return false;
                }
            }

            if (createdByFilters && createdByFilters.length) {
                if (!createdByFilters.find(userId => auditable.createdByUserId === userId)) {
                    return false;
                }
            }
            if (modifiedByFilters && modifiedByFilters.length) {
                if (!modifiedByFilters.find(userId => auditable.modifiedByUserId === userId)) {
                    return false;
                }
            }
            if (createdDateFilter) {
                if (!DateQueryModel.isInDateQuery(auditable.createdDate as Date, createdDateFilter, now, this.datefilterBufferMinutes)) {
                    return false;
                }
            }
            if (modifiedDateFilter) {
                if (!DateQueryModel.isInDateQuery(auditable.modifiedDate as Date, modifiedDateFilter, now, this.datefilterBufferMinutes)) {
                    return false;
                }
            }

            return true;
        });
    }
}
